import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, Filter, Globe, Images, Loader2, Trash } from 'lucide-react';
import { Edit, MoreHoriz } from 'iconoir-react';
import { useTranslation } from 'react-i18next';

import { useQueryClient } from '@tanstack/react-query';

import { Button } from 'components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
  dropdownMenuItemDestructiveClass,
} from 'components/ui/dropdown-menu';
import { ChatInput } from 'features/chat/components/ChatInput';
import { ButtonIds, initialFeaturesState, type Attachment } from 'features/chat/types/chat';
import { useGetAgentById } from 'features/chat/hooks/use-agents';
import {
  invalidateAgentConversationList,
  prependAgentConversationToCache,
} from 'features/chat/utils/agent-sidebar-sync';
import { useChatStore } from 'state/store/chat/use-chat-store';
import { useModelStore } from 'state/store/chat/use-model-store';
import { useConfig } from 'hooks/use-config';
import { useIsMobile } from 'hooks/use-mobile';
import { useToast } from 'hooks/use-toast';
import { useInviteAssistant } from 'features/group-chat/hooks/use-invite';
import type { InvitationPayload } from 'features/group-chat/services/group-services';
import { trackEvent } from 'analytics/tracker';
import { EVENTS, InitialPromptType } from 'analytics/events';
import { Alignment, ButtonTypes, type ChatInputButton } from 'pages/chat/chat';
import { DEFAULT_MAIN_CHAT_AGENT_ID } from 'utils/constants/chat-constants';
import { EditScheduledJobModal } from './components/edit-scheduled-job-modal';
import { useDeleteScheduledJob, useGetScheduledJobs } from './hooks/use-scheduled-jobs';
import type { ScheduledJobItem, ScheduledJobStatus } from './services/scheduled-jobs.types';

const STATUS_FILTER_OPTIONS: {
  value: ScheduledJobStatus;
  labelKey: string;
  defaultLabel: string;
}[] = [
    { value: 'active', labelKey: 'ACTIVE', defaultLabel: 'Active' },
    { value: 'paused', labelKey: 'PAUSED', defaultLabel: 'Paused' },
    { value: 'completed', labelKey: 'COMPLETED', defaultLabel: 'Completed' },
    { value: 'expired', labelKey: 'EXPIRED', defaultLabel: 'Expired' },
    { value: 'failed', labelKey: 'FAILED', defaultLabel: 'Failed' },
  ];

export default function SchedulerPage() {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { configurationSettings } = useConfig();
  const sendMessage = useChatStore((state) => state.sendMessage);
  const setActiveConversationId = useChatStore((state) => state.setActiveConversationId);
  const selectedModel = useModelStore((state) => state.selectedModel);
  const inviteAssistantMutation = useInviteAssistant();
  const deleteScheduledJobMutation = useDeleteScheduledJob();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const models = useModelStore((state) => state.models);
  const [statusFilter, setStatusFilter] = useState<ScheduledJobStatus>('active');
  const [editingJob, setEditingJob] = useState<ScheduledJobItem | null>(null);

  const [isFeatureSelected, setIsFeatureSelected] =
    useState<Record<ButtonIds, boolean>>(initialFeaturesState);
  const [selectedSources, setSelectedSources] = useState<string[]>([]);
  const [selectedFolderIdsBySource, setSelectedFolderIdsBySource] = useState<
    Record<string, string[]>
  >({});
  const [isSourcesToolActive, setIsSourcesToolActive] = useState(false);
  const [selectedTools, setSelectedTools] = useState<string[]>([]);

  // Agent selection: mirrors chat.tsx — the picked agent (not a model) answers the
  // new conversation, and its model is shown/locked in the input's model selector.
  const [selectedAgentId, setSelectedAgentId] = useState<string | null>(null);

  const { data: selectedAgentData, isError: isAgentDetailError } = useGetAgentById(
    { id: selectedAgentId ?? '' },
    { enabled: !!selectedAgentId }
  );
  const selectedAgentLlm = selectedAgentData?.agent?.llm_config;
  const agentModelLoading = !!selectedAgentId && !selectedAgentData && !isAgentDetailError;
  const agentUploadModel = useMemo(() => {
    if (!selectedAgentLlm) return null;
    const { model_id, model_name } = selectedAgentLlm;
    const normalizedName = model_name?.toLowerCase();
    return (
      models.find(
        (m) =>
          (model_id && m.id === model_id) ||
          (model_name && (m.id === model_name || m.name === model_name)) ||
          (normalizedName &&
            (m.id.toLowerCase() === normalizedName || m.name.toLowerCase() === normalizedName)),
      ) ?? null
    );
  }, [selectedAgentLlm, models]);
  const agentModelLabel = agentUploadModel?.name ?? selectedAgentLlm?.model_name ?? null;

  const selectedStatusOption =
    STATUS_FILTER_OPTIONS.find((option) => option.value === statusFilter) ??
    STATUS_FILTER_OPTIONS[0];

  // Recent: always the 3 newest active jobs by createdDate.
  const {
    data: recentData,
    isLoading: isRecentLoading,
    isError: isRecentError,
    isFetching: isRecentFetching,
  } = useGetScheduledJobs({
    status: 'active',
    skip: 0,
    take: 3,
    sort: 'createdDate',
    order: 'desc',
  });

  // Status-filtered list: all jobs for the selected status.
  const {
    data: statusData,
    isLoading: isStatusLoading,
    isError: isStatusError,
    isFetching: isStatusFetching,
  } = useGetScheduledJobs({
    status: statusFilter,
    skip: 0,
    take: 25,
    sort: statusFilter === 'active' ? 'nextRunAt' : 'lastRunAt',
    order: statusFilter === 'active' ? 'asc' : 'desc',
  });

  const recentJobs = recentData?.items ?? [];
  const statusJobs = statusData?.items ?? [];

  // ChatInput only renders its footer toolbar (plus menu, tools, mic, send)
  // when inputButtons is non-empty — same set as project-page/chat.
  const inputButtons = useMemo<ChatInputButton[]>(
    () => [
      {
        id: ButtonIds.SEARCH,
        label: t('SEARCH'),
        icon: <Globe size={16} />,
        onClick: () => {
          setIsFeatureSelected((prev) => ({
            ...initialFeaturesState,
            [ButtonIds.SEARCH]: !prev[ButtonIds.SEARCH],
          }));
        },
        type: isMobile ? ButtonTypes.ICON : ButtonTypes.BUTTON,
        alignment: Alignment.LEFT,
        isDisabled: isFeatureSelected[ButtonIds.CREATE_IMAGE] || selectedSources.length > 0,
        isSelected: isFeatureSelected[ButtonIds.SEARCH],
      },
      {
        id: ButtonIds.CREATE_IMAGE,
        label: t('CREATE_IMAGE'),
        icon: <Images size={16} />,
        onClick: () => {
          setIsFeatureSelected((prev) => ({
            ...initialFeaturesState,
            [ButtonIds.CREATE_IMAGE]: !prev[ButtonIds.CREATE_IMAGE],
          }));
        },
        type: isMobile ? ButtonTypes.ICON : ButtonTypes.BUTTON,
        alignment: Alignment.LEFT,
        isDisabled: selectedSources.length > 0,
        isSelected: isFeatureSelected[ButtonIds.CREATE_IMAGE],
      },
    ],
    [isMobile, isFeatureSelected, t, selectedSources.length]
  );

  // Detach from any previous chat so this landing composer is not bound to it.
  useEffect(() => {
    setActiveConversationId(null);
  }, [setActiveConversationId]);

  // Same flow as project-page/custom-gpt-page: create a conversation, send the
  // message through the chat store, then redirect to the chat page.

  const handleSchedule = async (content: string, files: Attachment[]) => {
    if (!content.trim() && files.length === 0) return;

    setActiveConversationId(null);

    const payload: InvitationPayload = selectedAgentId
      ? {
        invitationType: 'ADD_USER',
        agentId: selectedAgentId,
      }
      : {
        invitationType: 'ADD_USER',
        agentId: DEFAULT_MAIN_CHAT_AGENT_ID,
        assistantId: selectedModel?.id,
      };

    try {
      const response = await inviteAssistantMutation.mutateAsync(payload);
      if (!response?.conversationId) return;

      const newConversationId = response.conversationId;
      trackEvent(EVENTS.NEW_CHAT_STARTED(newConversationId, InitialPromptType.TEXT));

      await sendMessage(
        content,
        configurationSettings,
        newConversationId,
        files,
        isFeatureSelected,
        false, // isGroupChatParticipants
        undefined, // parentIds
        undefined, // sources
        false, // isRegenerate
        undefined, // originalRegenerationMessageId
        undefined, // mentions
        undefined, // parentUserGenerationId
        false, // isIncognitoChat
        undefined, // customGptId
        selectedTools,
        undefined, // selectedSkillIds
        selectedAgentId ?? undefined,
      );

      if (selectedAgentId) {
        prependAgentConversationToCache(
          queryClient,
          selectedAgentId,
          configurationSettings?.blocksKey || '',
          newConversationId
        );
        invalidateAgentConversationList(queryClient);
      }

      navigate(`/chat/${newConversationId}`, {
        state: {
          preservedFeatures: isFeatureSelected,
          preservedSources: selectedSources,
          preservedFolderIdsBySource: selectedFolderIdsBySource,
          preservedSourcesToolActive: isSourcesToolActive,
          preservedTools: selectedTools,
        },
      });
    } catch (error) {
      console.error('Failed to start chat from scheduler:', error);
    }
  };

  const handleDeleteJob = async (job: ScheduledJobItem) => {
    try {
      await deleteScheduledJobMutation.mutateAsync(job.id);
      if (editingJob?.id === job.id) {
        setEditingJob(null);
      }
      toast({
        title: t('SCHEDULED_JOB_DELETED', { defaultValue: 'Scheduled job deleted' }),
        description: t('SCHEDULED_JOB_DELETED_DESCRIPTION', {
          defaultValue: 'The job was removed. Run history is preserved.',
        }),
      });
    } catch (error) {
      console.error('Failed to delete scheduled job:', error);
      toast({
        variant: 'destructive',
        title: t('FAILED_TO_DELETE_SCHEDULED_JOB', {
          defaultValue: 'Failed to delete scheduled job',
        }),
      });
    }
  };

  const renderJobRow = (job: ScheduledJobItem) => (
    <div
      key={job.id}
      role="button"
      tabIndex={0}
      className="group relative flex w-full cursor-pointer items-start gap-3 rounded-lg border border-transparent px-[5px] pr-14 text-left transition-colors hover:bg-muted/50"
      onClick={() => setEditingJob(job)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          setEditingJob(job);
        }
      }}
    >
      <span className="inline-flex size-5 shrink-0 overflow-hidden">
        <Clock
          className="size-5 text-grey-500 dark:text-muted-foreground"
          strokeWidth={1.5}
          aria-hidden
        />
      </span>

      <span className="flex min-w-0 flex-1 flex-col gap-2 leading-[1.2]">
        <span className="truncate text-base font-semibold text-grey-900 dark:text-foreground">
          {job.name}
        </span>
        {job.description ? (
          <span className="truncate text-sm font-normal text-grey-500 dark:text-muted-foreground">
            {job.description}
          </span>
        ) : null}
      </span>

      <div className="pointer-events-none absolute right-[5px] top-1/2 flex -translate-y-1/2 items-center gap-3 opacity-0 transition-opacity group-hover:pointer-events-auto group-hover:opacity-100 group-focus-within:pointer-events-auto group-focus-within:opacity-100">
        <button
          type="button"
          aria-label={t('EDIT', { defaultValue: 'Edit' })}
          className="inline-flex size-5 items-center justify-center text-grey-500 hover:text-grey-900 dark:text-muted-foreground dark:hover:text-foreground"
          onClick={(event) => {
            event.stopPropagation();
            setEditingJob(job);
          }}
        >
          <Edit width={20} height={20} strokeWidth={1.5} aria-hidden />
        </button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label={t('MORE_OPTIONS', { defaultValue: 'More options' })}
              className="inline-flex size-5 items-center justify-center text-grey-500 hover:text-grey-900 dark:text-muted-foreground dark:hover:text-foreground"
              onClick={(event) => event.stopPropagation()}
            >
              <MoreHoriz width={20} height={20} strokeWidth={1.5} aria-hidden />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-[180px]" onClick={(e) => e.stopPropagation()}>
            <DropdownMenuItem
              className={dropdownMenuItemDestructiveClass}
              disabled={deleteScheduledJobMutation.isPending}
              onSelect={(event) => {
                event.preventDefault();
                void handleDeleteJob(job);
              }}
            >
              <Trash className="h-4 w-4" />
              {t('DELETE', { defaultValue: 'Delete' })}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );

  const renderJobList = ({
    jobs,
    isLoading,
    isError,
    emptyMessage,
  }: {
    jobs: ScheduledJobItem[];
    isLoading: boolean;
    isError: boolean;
    emptyMessage: string;
  }) => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center gap-2 rounded-lg border border-dashed p-6 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          {t('LOADING_SCHEDULED_JOBS', { defaultValue: 'Loading scheduled jobs…' })}
        </div>
      );
    }

    if (isError) {
      return (
        <div className="rounded-lg border border-dashed p-6 text-center text-sm text-destructive">
          {t('FAILED_TO_LOAD_SCHEDULED_JOBS', {
            defaultValue: 'Failed to load scheduled jobs.',
          })}
        </div>
      );
    }

    if (jobs.length === 0) {
      return (
        <div className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
          {emptyMessage}
        </div>
      );
    }

    return <div className="flex flex-col gap-4">{jobs.map(renderJobRow)}</div>;
  };

  return (
    <div className="flex min-h-0 flex-col overflow-y-auto overflow-x-hidden">
      <div className="mx-auto w-full max-w-[720px] px-4 py-6">
        <div className="flex items-start justify-between gap-4">
          <div className="flex min-w-0 flex-col gap-2">
            <h1 className="text-2xl font-medium leading-[1.2] tracking-tight text-grey-900 dark:text-foreground">
              {t('SCHEDULED', { defaultValue: 'Scheduled' })}
            </h1>
            <p className="text-sm font-normal leading-[1.2] text-grey-900 dark:text-muted-foreground">
              {t('SCHEDULER_PAGE_SUBTITLE', {
                defaultValue: 'Ask Klax! to schedule tasks, set reminders, or monitor for updates.',
              })}
            </p>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                className="h-auto shrink-0 gap-1 rounded-lg border border-transparent bg-grey-50 px-3 py-3 text-sm font-normal leading-[1.2] text-grey-900 hover:bg-grey-100 dark:border-border dark:bg-muted dark:text-foreground dark:hover:bg-muted/80 dark:hover:text-foreground"
              >
                <Filter
                  className="h-4 w-4 shrink-0 text-grey-500 dark:text-muted-foreground"
                  strokeWidth={1.75}
                />
                {t(selectedStatusOption.labelKey, {
                  defaultValue: selectedStatusOption.defaultLabel,
                })}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[160px]">
              <DropdownMenuRadioGroup
                value={statusFilter}
                onValueChange={(value) => setStatusFilter(value as ScheduledJobStatus)}
              >
                {STATUS_FILTER_OPTIONS.map((option) => (
                  <DropdownMenuRadioItem key={option.value} value={option.value}>
                    {t(option.labelKey, { defaultValue: option.defaultLabel })}
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="mt-6">
          <ChatInput
            onSendMessage={handleSchedule}
            isLoading={inviteAssistantMutation.isPending}
            inputButtons={inputButtons}
            isCurrentResponseGenerating={false}
            isFeatureSelected={isFeatureSelected}
            selectedSources={selectedSources}
            onSourcesChange={setSelectedSources}
            selectedFolderIdsBySource={selectedFolderIdsBySource}
            onSelectedFolderIdsBySourceChange={setSelectedFolderIdsBySource}
            isSourcesToolActive={isSourcesToolActive}
            onToggleTool={(toolId) => {
              setIsFeatureSelected((prev) => ({
                ...prev,
                [toolId]: !prev[toolId],
              }));
            }}
            onToggleSourcesTool={() => setIsSourcesToolActive((prev) => !prev)}
            isCancelOnGoing={false}
            isModelSelectionDisabled
            selectedTools={selectedTools}
            onSelectedToolsChange={setSelectedTools}
            showQuickActions={false}
            selectedAgentId={selectedAgentId}
            onAgentChange={(agentId) => setSelectedAgentId(agentId)}
            showAgentSelector
            agentModelLabel={agentModelLabel}
            agentModelLoading={agentModelLoading}
            agentModelRegion={agentUploadModel?.region}
            agentUploadModel={agentUploadModel}
          />
        </div>

        <div className="mt-6 flex flex-col gap-4 border-b border-grey-100 pb-8 dark:border-border">
          <div className="flex items-center gap-2">
            <p className="text-sm font-semibold leading-[1.2] text-grey-900 dark:text-foreground">
              {t('RECENT', { defaultValue: 'Recent' })}
            </p>
            {isRecentFetching && !isRecentLoading ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
            ) : null}
          </div>
          {renderJobList({
            jobs: recentJobs,
            isLoading: isRecentLoading,
            isError: isRecentError,
            emptyMessage: t('NO_RECENT_ITEMS', { defaultValue: 'No recent items.' }),
          })}
        </div>

        <div className="mt-8 flex flex-col gap-4">
          <div className="flex items-center gap-2">
            <p className="text-sm font-semibold leading-[1.2] text-grey-900 dark:text-foreground">
              {t(selectedStatusOption.labelKey, {
                defaultValue: selectedStatusOption.defaultLabel,
              })}
            </p>
            {isStatusFetching && !isStatusLoading ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
            ) : null}
          </div>
          {renderJobList({
            jobs: statusJobs,
            isLoading: isStatusLoading,
            isError: isStatusError,
            emptyMessage: t('NO_SCHEDULED_JOBS_FOR_STATUS', {
              defaultValue: 'No {{status}} items.',
              status: t(selectedStatusOption.labelKey, {
                defaultValue: selectedStatusOption.defaultLabel,
              }).toLowerCase(),
            }),
          })}
        </div>
      </div>

      <EditScheduledJobModal
        open={!!editingJob}
        job={editingJob}
        onOpenChange={(open) => {
          if (!open) setEditingJob(null);
        }}
        onPromptUpdated={(job, prompt, hilEnabled) => {
          setEditingJob((current) =>
            current?.id === job.id
              ? { ...current, prompt, description: prompt, hilEnabled }
              : current
          );
        }}
        onOpenChat={(job) => {
          setEditingJob(null);
          if (job.conversationId) {
            navigate(`/chat/${job.conversationId}`);
            return;
          }
          if (job.agentId) {
            navigate(`/chat?agentId=${encodeURIComponent(job.agentId)}`);
          }
        }}
      />
    </div>
  );
}
